/**
 * The shared surfaces' view models: what ZhExecutionNode, ZhDecisionCard, ZhChatThread, ZhRunTimeline,
 * and ZhKpiLight draw. Each is shaped after the portal's own data so the portal can feed it live — run
 * and gate rows, chat messages, execution nodes — and the marketing site feeds it the example company.
 * The components fetch nothing and navigate nowhere: data in, drawing out, actions through slots.
 *
 * Portal sources: apps/portal/src/lib/executionGraph.ts (node tones and words), apps/portal/src/api.ts
 * (GateRow, ChatMessageRow, RunDetail), packages/shared/src/executions.ts (ExecutionTaskNode), and #690's
 * packages/shared/src/memory-graph.ts (the knowledge graph's nodes and edges).
 */

// ── KPI health (BG §18.4, KPI health; docs/performance.md) ─────────────────────────────────────────

/** A KPI read against its target: green on target, amber at risk, red off target. */
export type KpiRag = 'red' | 'amber' | 'green'

/** The light's words: its accessible name, and a legend's text. */
export const KPI_WORD: Readonly<Record<KpiRag, string>> = {
    green: 'On target',
    amber: 'At risk',
    red: 'Off target',
}

/** Legend order: the way a light is read, best first. */
export const KPI_ORDER: readonly KpiRag[] = ['green', 'amber', 'red']

/** A KPI view sortable by sortKpiViews: at minimum its name, its light, and whether it is overdue. */
export interface KpiSortable {
    name: string
    /** `null` for a KPI with no light yet: nothing read, or no target to read it against. */
    rag: KpiRag | null
    /** Stale past its own cadence, or never read at all. Optional: a caller with no cadence yet omits it. */
    overdue?: boolean
}

/**
 * AMBER, RED and overdue first (worst first, #898); on-target, on-time KPIs last. Ties broken by name.
 * Pure: HQ, the team page and the teams list each sort the board they already fetched, never fetch one
 * of their own.
 */
export function sortKpiViews<T extends KpiSortable>(views: readonly T[]): T[] {
    return [...views].sort(
        (left, right) => attentionRank(left) - attentionRank(right) || left.name.localeCompare(right.name),
    )
}

/** 0 for a KPI wanting attention — off target, at risk, stale, or with no light yet — and 1 for one on target and on time. */
function attentionRank(view: KpiSortable): number {
    return view.rag !== 'green' || view.overdue === true ? 0 : 1
}

// ── Layers ────────────────────────────────────────────────────────────────────────────────────────

/**
 * A layer's shape: a triangle for the company, a circle for a team, a five-pointed star for a member, a hexagon for
 * a role, a square for a task, a parallelogram for an execution, a ring for a run.
 */
export type LayerShape = 'triangle' | 'circle' | 'star' | 'hexagon' | 'square' | 'parallelogram' | 'ring'

// ── Executions (apps/portal/src/lib/executionGraph.ts) ─────────────────────────────────────────────

/** ExecutionNodeTone, as the portal derives it from a task's runs. */
export type ExecutionNodeTone =
    | 'unvisited'
    | 'running'
    | 'action'
    | 'paused'
    | 'queued'
    | 'succeeded'
    | 'failed'
    | 'cancelled'

/** The words the portal's graph shows for each tone, where the caller has none of its own. */
export const EXECUTION_TONE_WORD: Readonly<Record<ExecutionNodeTone, string>> = {
    unvisited: 'Not visited',
    running: 'Running',
    action: 'Awaiting approval',
    paused: 'Paused',
    queued: 'Queued',
    succeeded: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
}

/** Who a node, a message, or a card is from: a member of the enterprise. */
export interface SurfaceMember {
    name: string
    /** An image, where the member has one; otherwise the avatar is their initials. */
    avatarUrl?: string | null
}

/** One task in an execution: ExecutionTaskNode's labels with ExecutionNodeState's tone, word, and count. */
export interface ExecutionNodeView {
    name: string
    slug: string
    member?: SurfaceMember | null
    role?: { name: string } | null
    team?: { name: string } | null
    tone: ExecutionNodeTone
    /** The state in words ("Awaiting approval"). Defaults to the portal graph's word for the tone. */
    label?: string
    runCount: number
    /** Runs still going; more than one is counted beside the runs. */
    activeCount?: number
    /** The task has gone (deleted, or out of view) but its runs remain: drawn dashed, and said. */
    missing?: boolean
}

/** taken: a run went this way. current: its target run is still active. idle: not taken (yet). */
export type ExecutionEdgeState = 'taken' | 'current' | 'idle'

/** A member's initials for an avatar with no image: the first two letters of the first name. */
export function initials(name: string): string {
    return name.trim().slice(0, 2).toUpperCase()
}

// ── Decisions (apps/portal/src/api.ts GateRow) ─────────────────────────────────────────────────────

/** One heading of a gate's summary, and its lines. */
export interface DecisionSection {
    heading?: string
    lines: readonly string[]
}

/** A gate waiting on a human, written out: what it will do, why it stopped, and exactly what goes out. */
export interface DecisionView {
    /** What approving it does — the gate summary's headline, or the task's name. */
    title: string
    /** Why it stopped: what makes it leave the building, or the line it crossed. */
    why?: string
    /** Exactly what goes out: the summary's lines. */
    details?: readonly string[]
    /**
     * The summary as the run wrote it, under its own headings ("What changed", "Watch out for"). A section
     * without a heading is text that came before the first one.
     */
    sections?: readonly DecisionSection[]
    /** Who is asking: the team (or the execution), set in full ink. */
    from?: string
    /** And the member (or the task), after it. */
    by?: string
    /** How long it has waited, already in words ("2 hours"). */
    waited?: string
}

/** A decision just made on a gate (apps/portal/src/api.ts GateDecision), as its card shows it before it leaves. */
export type DecisionOutcome = 'approve' | 'reject' | 'request_changes'

/**
 * How a gate ended, as a card that keeps it shows it for good (ZhDecisionCard `settled`): one of the decisions, a
 * question `answered`, or `closed` for a gate that ended without one (it timed out, or was cancelled with its run).
 */
export type SettledOutcome = DecisionOutcome | 'answered' | 'closed'

/**
 * What deciding a gate does (#1760), as its card says it (ZhDecisionCard `kind`): an `action` runs something when it
 * is approved; a `question` is answered, and only informs the run.
 */
export type DecisionKind = 'action' | 'question'

// ── Chat (apps/portal/src/api.ts ChatMessageRow; apps/portal/src/lib/chat.ts) ──────────────────────

/**
 * One entry in a thread: a message from you or the member, a run it started, or a decision it raised. `id`
 * keys it (the message row's id, in the portal). An assistant entry with no text is a turn where the member
 * worked without saying anything; what it did is the caller's to show, through `message-extra`. A user entry's
 * `sender` is the person who wrote it, where several people share the thread: drawn as their avatar beside it.
 */
export type ChatEntry =
    | { kind: 'user'; text: string; id?: string; sender?: SurfaceMember | null }
    | { kind: 'assistant'; text: string; id?: string }
    | { kind: 'run'; task: string; who: string; id?: string }
    | { kind: 'decision'; decision: DecisionView; id?: string }
    /**
     * A line from the OS in the thread, not a message from anyone (#283): how work the member started ended, say.
     * Drawn as one muted system label on its own row, not a bubble; `system-entry` (scoped by the entry) draws it
     * instead, for a caller that adds a link beside it.
     */
    | { kind: 'system'; text: string; id?: string }

/** One conversation in the list: the member, and the last thing said. */
export interface ChatListItem {
    /** Keys the row, and tells the caller which was picked. */
    id?: string
    member: SurfaceMember
    role: string
    last: string
    /** The thread on show. */
    open?: boolean
    /** How many replies you have not read. None (or 0) draws no marker. */
    unread?: number
}

// ── Run record (apps/portal/src/api.ts RunDetail events) ───────────────────────────────────────────

/**
 * done: it happened. action: it waited on a human. note: the OS said something, nothing changed. failed:
 * where the run stopped, failed or blocked.
 */
export type RunStepTone = 'done' | 'action' | 'note' | 'failed'

/** One event on a run's record: its type as the API names it, when, and what it means in words. */
export interface RunStepView {
    /** Keys the step: the event's id, in the portal. */
    id?: string
    type: string
    at: string
    /** What it means in words. The portal's raw events have none but the one that says why the run stopped. */
    title?: string
    detail?: string
    tone: RunStepTone
    /** The step a gate is still waiting at: the decision is drawn (or offered) inline. */
    decide?: boolean
}

// ── Knowledge (#690: packages/shared/src/memory-graph.ts, GET /memory) ──────────────────────────────────────────

/**
 * What a note is. fact and observation are evidenced directly; a preference is someone's stated choice; a
 * correction supersedes an earlier note; an inference is drawn from a pattern across others and is never proof.
 */
export type KnowledgeType = 'fact' | 'observation' | 'preference' | 'correction' | 'inference'

export const KNOWLEDGE_TYPE_WORD: Readonly<Record<KnowledgeType, string>> = {
    fact: 'Fact',
    observation: 'Observation',
    preference: 'Preference',
    correction: 'Correction',
    inference: 'Inference',
}

/** Whether a note stands, has been superseded by a correction (it evolved), or is excluded from what tasks ask. */
export type KnowledgeState = 'active' | 'corrected' | 'excluded'

export const KNOWLEDGE_STATE_WORD: Readonly<Record<KnowledgeState, string>> = {
    active: 'In use',
    corrected: 'Superseded',
    excluded: 'Excluded',
}

/** What an edge means. similar-to is shared wording only: never proof, never a supersession. */
export type KnowledgeRelation = 'shares-task' | 'shares-team' | 'supersedes' | 'similar-to'

export const KNOWLEDGE_RELATION_WORD: Readonly<Record<KnowledgeRelation, string>> = {
    supersedes: 'Supersedes',
    'shares-task': 'Same task',
    'shares-team': 'Same team',
    'similar-to': 'Similar wording, not proof',
}

/** A team, project, or task label: organisation-wide grouping, never private storage. */
export interface KnowledgeLabel {
    kind: 'team' | 'project' | 'task'
    name: string
}

/** One note, shaped after #690's MemoryGraphNode so the portal can hand its rows straight in. */
export interface KnowledgeNodeView {
    id: string
    body: string
    knowledgeType: KnowledgeType
    correctionState: KnowledgeState
    labels: readonly KnowledgeLabel[]
    /** Where it came from, in words: the source a reader can check. */
    provenance?: string | null
    /** When it was recorded, already in words ("Mar 20"). */
    recorded?: string
}

/** One labelled relationship, as #690's MemoryGraphEdge: from `source` to `target`. */
export interface KnowledgeEdgeView {
    id: string
    source: string
    target: string
    type: KnowledgeRelation
}

// ── The memory constellation (packages/shared/src/memory-constellation.ts, GET /memory/constellation) ───────────

/**
 * What a link between two memories says. semantic is similar meaning and temporal is closeness in time: neither is
 * proof of anything. entity is a person, place or thing both mention. causal is one memory explaining another, as
 * the memory store extracted it.
 */
export type ConstellationLinkKind = 'semantic' | 'temporal' | 'entity' | 'causal'

export const CONSTELLATION_LINK_WORD: Readonly<Record<ConstellationLinkKind, string>> = {
    semantic: 'Similar meaning, not proof',
    temporal: 'Close in time',
    entity: 'Same entity',
    causal: 'Explains',
}

/** One memory in the constellation: a dot, coloured by when it was mentioned and sized by what stands behind it. */
export interface ConstellationUnitView {
    id: string
    text: string
    /** What kind of memory it is, in the page's words ("World", "Observation"). */
    typeWord: string
    /** When it was mentioned, in epoch milliseconds, or null when the store does not say. */
    at: number | null
    /** The same moment in words, as the page writes dates. */
    atWord?: string
    /** How many source facts it was consolidated from; absent for a memory that is itself a fact. */
    sourceFacts?: number | null
    /** The people, places and things it mentions. */
    entities: readonly string[]
    /** False for a memory with nothing to open: picking it says nothing. */
    opens?: boolean
}

/** One link between two memories, from `source` to `target`. */
export interface ConstellationLinkView {
    id: string
    source: string
    target: string
    type: ConstellationLinkKind
    /** The entity both ends mention, on an entity link. */
    entity?: string
}
